/**
 * PatientRegistrationPage Component
 *
 * Main page component for the patient registration workflow.
 * Integrates with the PatientRegistrationWizard and existing Layout components.
 * Provides responsive design and SEO optimization.
 * Follows the exact pattern from DieticianRegistrationPage.
 *
 * @module components/pages/PatientRegistrationPage
 */

import React, { useState, useEffect } from 'react';
import { cn } from '@/utils/cn';
import { Layout } from '@/components/layout/Layout';
import { PatientRegistrationWizard } from '@/components/patient-registration/PatientRegistrationWizard';
import { useTheme } from '@/hooks/useTheme';
import { BodyText, TitleText } from '../ui';

/**
 * PatientRegistrationPage component props interface
 */
export interface PatientRegistrationPageProps {
  /** Custom class name */
  className?: string;
  /** Callback when registration completes */
  onRegistrationComplete?: (registrationId: string) => void;
}

/**
 * Step-specific content for SEO and accessibility
 */
const STEP_CONTENT = {
  1: {
    title: 'Create Your Patient Account',
    description: 'Join BiteWise to connect with qualified dietitians and start your nutrition journey'
  },
  2: {
    title: 'Complete Your Profile',
    description: 'Add your personal details and health information'
  }
};

/**
 * PatientRegistrationPage component for the complete registration workflow
 *
 * @example
 * ```tsx
 * <PatientRegistrationPage
 *   onRegistrationComplete={(registrationId) => {
 *     analytics.track('patient_registration_complete', { registrationId });
 *     navigate('/patient/dashboard');
 *   }}
 * />
 * ```
 */
export const PatientRegistrationPage: React.FC<PatientRegistrationPageProps> = ({
  className,
  onRegistrationComplete
}) => {
  const { isDark } = useTheme();
  const [currentStep, setCurrentStep] = useState<number>(1);
  const [step1Title, setStep1Title] = useState<string>('Create Your Patient Account');

  /**
   * Check for referralData in session storage and update step 1 title accordingly
   */
  useEffect(() => {
    const referralData = sessionStorage.getItem('referralData');
    if (referralData) {
      setStep1Title('Choose your password');
    } else {
      setStep1Title('Create Your Patient Account');
    }
  }, []);

  /**
   * Handle step changes for analytics and SEO
   */
  const handleStepChange = (step: number) => {
    setCurrentStep(step);

    // Update page title for better UX
    const stepContent = STEP_CONTENT[step as keyof typeof STEP_CONTENT];
    if (stepContent && typeof document !== 'undefined') {
      document.title = `${stepContent.title} - BiteWise`;
    }
  };

  /**
   * Handle registration completion
   */
  const handleRegistrationComplete = (registrationId: string) => {
    onRegistrationComplete?.(registrationId);
  };

  /**
   * Get current step content for accessibility
   */
  const getCurrentStepContent = () => {
    const stepContent = STEP_CONTENT[currentStep as keyof typeof STEP_CONTENT] || STEP_CONTENT[1];

    // Override step 1 title if we have a dynamic title
    if (currentStep === 1) {
      return {
        ...stepContent,
        title: step1Title
      };
    }

    return stepContent;
  };

  const stepContent = getCurrentStepContent();

  return (
    <>
      <Layout className={cn('min-h-screen', className)}>
        {/* Page Header */}
          <div className="container mx-auto px-4 py-8">
            <div className="max-w-3xl mx-auto text-center">
              <TitleText>
                {stepContent.title}
              </TitleText>
              <BodyText
                variant="secondary"
                className="text-lg"
              >
                {stepContent.description}
              </BodyText>

              {/* Progress Indicator for Screen Readers */}
              <div className="sr-only" aria-live="polite" aria-atomic="true">
                Currently on step: {currentStep} of 2
              </div>
            </div>
          </div>

        {/* Main Content */}
        <main className={cn(
          "flex-1",
          isDark ? "bg-secondary-900" : "bg-white"
        )}>
          <div className="container mx-auto px-4 py-4 sm:py-6">
            {/* Registration Wizard */}
            <PatientRegistrationWizard
              onComplete={handleRegistrationComplete}
              onStepChange={handleStepChange}
              className="max-w-4xl mx-auto"
            />

            {/* Additional Information */}
            <div className="max-w-2xl mx-auto mt-4">
              <div className={cn(
                "rounded-lg p-6",
                isDark ? "bg-secondary-900" : "bg-secondary-50"
              )}>
                <h2 className={cn(
                  "text-lg font-semibold mb-3",
                  isDark ? "text-secondary-100" : "text-secondary-900"
                )}>
                  Why Join BiteWise as a Patient?
                </h2>
                <ul className={cn(
                  "space-y-2 text-sm",
                  isDark ? "text-secondary-400" : "text-secondary-600"
                )}>
                  <li className="flex items-start">
                    <svg className={cn(
                      "w-4 h-4 mt-0.5 mr-2 flex-shrink-0",
                      isDark ? "text-success-400" : "text-success-600"
                    )} fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    Connect with qualified, verified dietitians in your area
                  </li>
                  <li className="flex items-start">
                    <svg className={cn(
                      "w-4 h-4 mt-0.5 mr-2 flex-shrink-0",
                      isDark ? "text-success-400" : "text-success-600"
                    )} fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    Receive personalised nutrition guidance and meal plans
                  </li>
                  <li className="flex items-start">
                    <svg className={cn(
                      "w-4 h-4 mt-0.5 mr-2 flex-shrink-0",
                      isDark ? "text-success-400" : "text-success-600"
                    )} fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    Track your progress and achieve your health goals
                  </li>
                  <li className="flex items-start">
                    <svg className={cn(
                      "w-4 h-4 mt-0.5 mr-2 flex-shrink-0",
                      isDark ? "text-success-400" : "text-success-600"
                    )} fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    Secure, HIPAA-compliant platform for your health data
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </main>
      </Layout>
    </>
  );
};

export default PatientRegistrationPage;